<script lang="ts">
	import { maxNumber, maxNumbers, minNumber } from "../../../../core/schema/productOptionsConstants.js";
	import cross from "../../../../core/assets/icons/cross.svg?raw";
	import { inflectNumberCount } from "./inflectNumberCount.js";
	import NumberInput from "../Form/NumberInput.svelte";
	import Icon from "../Icon.svelte";
	import type { ProductNumber } from "../../../../core/schema/ProductNumber.js";
	import plus from "../../../../core/assets/icons/btn-plus.svg?raw";
	import Button from "../Button.svelte";
	import cislodort from "../../../../core/assets/icons/cislodort.svg";
	import { inflectFit } from "./inflectFit.js";
	import ProductOptionHeader from "./ProductOptionHeader.svelte";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";
	import { calculatePriceForProductOption } from "../../../../core/schema/utils/calculatePriceForProductOption.js";
	import { inflectNumber } from "./inflectNumber.js";
	import Message from "../Message.svelte";

	export let numbers: ProductNumber[];
	export let updateNumbers: (numbers: ProductNumber[]) => void;
	export let basePrice: number;
	export let minimalPricesForOptions: Record<ProductOption, number>;
	export let changed: boolean | undefined;

	let newNumbers = structuredClone(numbers);
	let firstNumberHasBeenForced = false;

	function onChange(_numbers: typeof numbers): void {
		if (!firstNumberHasBeenForced) {
			return;
		}
		if (changed === undefined) {
			changed = false;
			return;
		}
		changed = true;
	}
	$: onChange(newNumbers);

	function addNumber(): void {
		newNumbers.push({ id: crypto.randomUUID(), number: 0 });
		newNumbers = newNumbers;
	}

	function removeNumber(idToRemove: string): void {
		newNumbers = newNumbers.filter(({ id }) => id !== idToRemove);
	}

	$: minimalPrice = minimalPricesForOptions[ProductOption.Numbers];
	$: currentPrice = calculatePriceForProductOption(basePrice, ProductOption.Numbers, newNumbers);
	$: numbersLeft = maxNumbers - newNumbers.length;

	function forceFirstNumber(numbers: ProductNumber[]): void {
		if (numbers.length === 0 && !firstNumberHasBeenForced) {
			addNumber();
			firstNumberHasBeenForced = true;
		}
	}

	$: forceFirstNumber(newNumbers);
</script>

<ProductOptionHeader name="Čokoládové číslice" image={cislodort} price={currentPrice} {minimalPrice}>
	Používáme čokoládové číslice z bílé nebo hořké čokolády, které si sami vyrábíme. Naše cukrářky zvolí daný druh číslic
	s ohledem na to, aby s vaším dortem ladily.
</ProductOptionHeader>

{#each newNumbers as { id, number }, index (id)}
	<div class="mb-4">
		<div>Zadejte věk oslavence</div>
		<div class="grid w-full grid-cols-[auto,1fr] items-center gap-x-3 gap-y-2">
			<span class="text-base font-semibold leading-5">{index + 1}.</span>
			<div class="flex w-full gap-2">
				<NumberInput
					id="number-{id}"
					class="flex-1 !gap-1"
					min={minNumber}
					max={maxNumber}
					required
					bind:valueAsNumber={number}
				/>

				<button
					type="button"
					on:click={() => {
						removeNumber(id);
					}}
				>
					<Icon
						icon={cross}
						class="aspect-square h-3 cursor-pointer text-secondary-250 transition-colors duration-300 hover:text-secondary-300"
					/>
				</button>
			</div>
		</div>
	</div>
{/each}
{#if numbersLeft > 0}
	<Message variant="info" class="mb-4">
		<p class="mb-2 mt-0.5 text-xs font-medium leading-tight">
			Na produkt se {inflectFit(numbersLeft)} ještě další {numbersLeft}
			{inflectNumber(numbersLeft)}.
		</p>

		{#if newNumbers.length > 0}
			<p class="text-2xs">
				Funkci <strong>přidat druhé číslo</strong> využijte jen v případě, kdy je dort určen
				<strong>více oslavencům</strong>, abychom Váš dort bezchybně nazdobili.
			</p>
		{/if}

		{#if numbersLeft > 0}
			<div class="mt-2 flex flex-col text-2xs">
				<Button type="button" on:click={addNumber} variant="secondary" class="mt-1 self-end">
					Přidat {inflectNumberCount[newNumbers.length + 1]} číslo
				</Button>
			</div>
		{/if}
	</Message>
{/if}

<div class="flex flex-col">
	<Button
		icon={plus}
		iconClass="w-5 h-5"
		class="mt-4 self-end"
		on:click={() => {
			updateNumbers(newNumbers);
		}}
	>
		Uložit úpravu
	</Button>
</div>
